import React, {useEffect} from 'react'
import {useDispatch} from 'react-redux'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {SimilarHotels} from 'components/CarouselWidgets/components/SimilarHotels'
import {CarouselWidgetsProvider} from 'components/CarouselWidgets/context/CarouselWidgetsContext'
import {useNavigateToSearchWithTrigger} from 'hooks/useNavigateToSearch'
import {getLocaleCode} from 'modules/meta/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {getIsAnchorHotel} from 'modules/sapiSearch/selectors'
import {metaLandingParamKeys} from 'modules/search/constants'
import {getIsNoDatesSearch} from 'modules/search/selectors'
import {SearchTrigger} from 'modules/searchBox/types'
import {omit} from 'ramda'
import {generateAccommodationPageURL} from 'utils/url'

import {Button} from '@vio/atlas/Button/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {
  useDispatchTeamEvent,
  useTrackingTeam
} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {useSuggestionsParameters} from './useSuggestionsParameters'

type SuggestedHotelsProps = {
  hotelId: string
  onHotelsLoadingComplete: (hasHotels: boolean) => void
  hasPadding?: boolean
  hasSideMargin?: boolean
  hasWhiteBackground?: boolean
  /** When true, defers SimilarHotels mount until the section enters the viewport. */
  isLazyLoaded?: boolean
}

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
  padding-bottom: ${cssTheme.layout.spacing.s500};
  padding-left: 0;
  padding-right: ${cssTheme.layout.spacing.s200};
  &.--has-padding-left {
    padding-left: ${cssTheme.layout.spacing.s500};
  }
`

const SeeAllText = styled.span`
  &.--mobile {
    color: ${cssTheme.colors.content.brand.primary};
  }
`

const getSearchNavigationOptions = (isMobile: boolean) =>
  isMobile
    ? {
        mergeWithCurrentParams: false,
        method: 'navigate' as const
      }
    : {
        mergeWithCurrentParams: true,
        method: 'open' as const,
        delay: TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
      }

const SimilarPropertiesWrapper = styled.div`
  &.--white-bg {
    background: ${cssTheme.colors.background.neutral.secondary};
  }
`

export const AccommodationViewSuggestedHotels = ({
  hotelId,
  onHotelsLoadingComplete,
  hasPadding,
  hasWhiteBackground,
  hasSideMargin = true,
  isLazyLoaded = false
}: SuggestedHotelsProps) => {
  const {isMobile} = useDeviceLayout()
  const similarPropertiesVisibility = isMobile

  const {anchorHotelId, placeId, rooms, checkIn, checkOut} =
    useSuggestionsParameters({stable: isMobile})

  const {ref, inView} = useInView({
    triggerOnce: true,
    threshold: 0.5,
    rootMargin: '50px 0px'
  })
  const shouldLoadContent = !isLazyLoaded || inView
  const dispatchTeamEvent = useDispatchTeamEvent()
  const dispatch = useDispatch()
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)

  const baseSearchParameters = {
    rooms,
    checkIn,
    checkOut,
    userSearch: '1' as const
  }
  const navigationOptions = getSearchNavigationOptions(isMobile)
  const navigateToSearch = useNavigateToSearchWithTrigger(
    SearchTrigger.AccommodationSuggestedHotels
  )

  const team = useTrackingTeam()
  const {getQueryParams} = useRouterProvider()
  const isAnchorHotel = useSelector(state => getIsAnchorHotel(state, hotelId))
  const locale = useSelector(getLocaleCode)

  useEffect(() => {
    if (inView)
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsSimilarHotelsSection,
          action: Action.Viewed
        })
      )
  }, [dispatchTeamEvent, inView])

  const onHotelClick = ({hotelId: clickedHotelId}: {hotelId: string}) => {
    if (similarPropertiesVisibility || !isMobile) {
      const params = omit(
        ['clickedOffer', 'clickedBundle', ...metaLandingParamKeys],
        getQueryParams()
      )

      setTimeout(() => {
        window.open(
          generateAccommodationPageURL(
            clickedHotelId,
            locale,
            params,
            isAnchorHotel
          ),
          '_blank'
        )
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
      return
    }

    if (anchorHotelId && anchorHotelId === clickedHotelId) {
      dispatch(hideOverlay())
      setTimeout(() => {
        window.scrollTo({top: 0, behavior: 'smooth'})
      }, 500)

      return
    }

    navigateToSearch(
      {...baseSearchParameters, hotelId: clickedHotelId},
      navigationOptions
    )
  }

  const onSeeAllClick = () => {
    navigateToSearch({...baseSearchParameters, hotelId}, navigationOptions)
  }

  const trackingPayload = {
    component: 'SimilarHotelAccomodationPage',
    payload: {accommodationPageHotelId: hotelId},
    team
  }

  return (
    <SimilarPropertiesWrapper
      className={cx(hasWhiteBackground && '--white-bg')}
    >
      <PageWidthContainer>
        <div ref={ref} data-body-scroll-lock-ignore>
          {shouldLoadContent && (
            <>
              <TopSection className={cx(hasPadding && '--has-padding-left')}>
                <Text variant="titleL">
                  <FormattedMessageWrapper
                    id="suggestion.similarProperties"
                    defaultMessage="Similar properties"
                  />
                </Text>

                <Button
                  variant={isMobile ? 'ghost' : 'tertiary'}
                  onClick={onSeeAllClick}
                >
                  <SeeAllText>
                    <FormattedMessageWrapper
                      id="hotelCard.seeAll"
                      defaultMessage="See all"
                    />
                  </SeeAllText>
                </Button>
              </TopSection>
              <CarouselWidgetsProvider>
                <SimilarHotels
                  hotelId={hotelId}
                  placeId={placeId}
                  rooms={rooms}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  trackingPayload={trackingPayload}
                  onHotelClick={onHotelClick}
                  onRequestComplete={hotelIds =>
                    onHotelsLoadingComplete(hotelIds.length > 0)
                  }
                  hasInsideMargin={hasSideMargin}
                  parentId="accommodation-page"
                  isNoDatesSearch={isNoDatesSearch}
                />
              </CarouselWidgetsProvider>
            </>
          )}
        </div>
      </PageWidthContainer>
    </SimilarPropertiesWrapper>
  )
}
